import _objectSpread from "/opt/build/repo/client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _createForOfIteratorHelper from "/opt/build/repo/client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _regeneratorRuntime from "/opt/build/repo/client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";
import _asyncToGenerator from "/opt/build/repo/client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _classCallCheck from "/opt/build/repo/client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/opt/build/repo/client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _asyncIterator from "/opt/build/repo/client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/asyncIterator";

/**
 * A client library for the https://nft.storage/ service. It provides a convenient
 * interface for working with the [Raw HTTP API](https://nft.storage/#api-docs)
 * from a web browser or [Node.js](https://nodejs.org/) and comes bundled with
 * TS for out-of-the box type inference and better IntelliSense.
 *
 * @example
 * ```js
 * import { NFTStorage, File, Blob } from "nft.storage"
 * const client = new NFTStorage({ token: API_TOKEN })
 *
 * const cid = await client.storeBlob(new Blob(['hello world']))
 * ```
 * @module
 */
import { transform } from 'streaming-iterables';
import pRetry from 'p-retry';
import { TreewalkCarSplitter } from 'carbites/treewalk';
import * as API from './lib/interface.js';
import * as Token from './token.js';
import { fetch, File, Blob, FormData } from './platform.js';
import { toGatewayURL } from './gateway.js';
var MAX_STORE_RETRIES = 5;
var MAX_CONCURRENT_UPLOADS = 3;
var MAX_CHUNK_SIZE = 1024 * 1024 * 10; // chunk to ~10MB CARs

/**
 * @implements API.Service
 */

var NFTStorage = /*#__PURE__*/function () {
  /**
   * Constructs a client bound to the given `options.token` and
   * `options.endpoint`.
   *
   * @example
   * ```js
   * import { NFTStorage, File, Blob } from "nft.storage"
   * const client = new NFTStorage({ token: API_TOKEN })
   *
   * const cid = await client.storeBlob(new Blob(['hello world']))
   * ```
   * Optionally you could pass an alternative API endpoint (e.g. for testing)
   * @example
   * ```js
   * import { NFTStorage } from "nft.storage"
   * const client = new NFTStorage({
   *   token: API_TOKEN
   *   endpoint: new URL('http://localhost:8080/')
   * })
   * ```
   *
   * @param {{token: string, endpoint?:URL}} options
   */
  function NFTStorage(_ref) {
    var token = _ref.token,
        _ref$endpoint = _ref.endpoint,
        endpoint = _ref$endpoint === void 0 ? new URL('https://api.nft.storage') : _ref$endpoint;

    _classCallCheck(this, NFTStorage);

    /**
     * Authorization token.
     *
     * @readonly
     */
    this.token = token;
    /**
     * Service API endpoint `URL`.
     * @readonly
     */

    this.endpoint = endpoint;
  }
  /**
   * @hidden
   * @param {string} token
   */


  _createClass(NFTStorage, [{
    key: "storeBlob",
    value: // Just a sugar so you don't have to pass around endpoint and token around.

    /**
     * Stores a single file and returns the corresponding Content Identifier (CID).
     * Takes a [Blob](https://developer.mozilla.org/en-US/docs/Web/API/Blob/Blob)
     * or a [File](https://developer.mozilla.org/en-US/docs/Web/API/File). Note
     * that no file name or file metadata is retained.
     *
     * @example
     * ```js
     * const content = new Blob(['hello world'])
     * const cid = await client.storeBlob(content)
     * cid //> 'zdj7Wn9FQAURCP6MbwcWuzi7u65kAsXCdjNTkhbJcoaXBusq9'
     * ```
     *
     * @param {Blob} blob
     */
    function storeBlob(blob) {
      return NFTStorage.storeBlob(this, blob);
    }
    /**
     * Stores files encoded as a single [Content Addressed Archive
     * (CAR)](https://github.com/ipld/specs/blob/master/block-layer/content-addressable-archives.md).
     *
     * Takes a [Blob](https://developer.mozilla.org/en-US/docs/Web/API/Blob/Blob)
     * or a [File](https://developer.mozilla.org/en-US/docs/Web/API/File).
     *
     * Returns the corresponding Content Identifier (CID).
     *
     * See the [`ipfs-car` docs](https://www.npmjs.com/package/ipfs-car) for more
     * details on packing a CAR file.
     *
     * @example
     * ```js
     * import { pack } from 'ipfs-car/pack'
     * import { CarReader } from '@ipld/car'
     * const { out, root } = await pack({
     *  input: fs.createReadStream('pinpie.pdf')
     * })
     * const expectedCid = root.toString()
     * const carReader = await CarReader.fromIterable(out)
     * const cid = await storage.storeCar(carReader)
     * console.assert(cid === expectedCid)
     * ```
     *
     * @example
     * ```
     * import { packToBlob } from 'ipfs-car/pack/blob'
     * const data = 'Hello world'
     * const { root, car } = await packToBlob({ input: [new TextEncoder().encode(data)] })
     * const expectedCid = root.toString()
     * const cid = await client.storeCar(car)
     * console.assert(cid === expectedCid)
     * ```
     * @param {Blob|API.CarReader} car
     * @param {{onStoredChunk?: (size: number) => void}} [options]
     */

  }, {
    key: "storeCar",
    value: function storeCar(car, options) {
      return NFTStorage.storeCar(this, car, options);
    }
    /**
     * Stores a directory of files and returns a CID for the directory.
     *
     * @example
     * ```js
     * const cid = await client.storeDirectory([
     *   new File(['hello world'], 'hello.txt'),
     *   new File([JSON.stringify({'from': 'incognito'}, null, 2)], 'metadata.json')
     * ])
     * cid //>
     * ```
     *
     * Argument can be a [FileList](https://developer.mozilla.org/en-US/docs/Web/API/FileList)
     * instance as well, in which case directory structure will be retained.
     *
     * @param {Iterable<File>} files
     */

  }, {
    key: "storeDirectory",
    value: function storeDirectory(files) {
      return NFTStorage.storeDirectory(this, files);
    }
    /**
     * Returns current status of the stored NFT by its CID. Note the NFT must
     * have previously been stored by this account.
     *
     * @example
     * ```js
     * const status = await client.status('zdj7Wn9FQAURCP6MbwcWuzi7u65kAsXCdjNTkhbJcoaXBusq9')
     * ```
     *
     * @param {string} cid
     */

  }, {
    key: "status",
    value: function status(cid) {
      return NFTStorage.status(this, cid);
    }
    /**
     * Removes stored content by its CID from the service.
     *
     * > Please note that even if content is removed from the service other nodes
     * that have replicated it might still continue providing it.
     *
     * @example
     * ```js
     * await client.delete('zdj7Wn9FQAURCP6MbwcWuzi7u65kAsXCdjNTkhbJcoaXBusq9')
     * ```
     *
     * @param {string} cid
     */

  }, {
    key: "delete",
    value: function _delete(cid) {
      return NFTStorage.delete(this, cid);
    }
    /**
     * Check if a CID of an NFT is being stored by nft.storage. Throws if the NFT
     * was not found.
     *
     * @example
     * ```js
     * const status = await client.check('zdj7Wn9FQAURCP6MbwcWuzi7u65kAsXCdjNTkhbJcoaXBusq9')
     * ```
     *
     * @param {string} cid
     */

  }, {
    key: "check",
    value: function check(cid) {
      return NFTStorage.check(this, cid);
    }
    /**
     * Stores the given token and all resources it references (in the form of a
     * File or a Blob) along with a metadata JSON as specificed in
     * [ERC-1155](https://eips.ethereum.org/EIPS/eip-1155#metadata). The
     * `token.image` must be either a `File` or a `Blob` instance, which will be
     * stored and the corresponding content address URL will be saved in the
     * metadata JSON file under `image` field.
     *
     * If `token.properties` contains properties with `File` or `Blob` values,
     * those also get stored and their URLs will be saved in the metadata JSON
     * file in their place.
     *
     * Note: URLs for `File` objects will retain file names e.g. in case of
     * `new File([bytes], 'cat.png', { type: 'image/png' })` will be transformed
     * into a URL that looks like `ipfs://bafy...hash/image/cat.png`. For `Blob`
     * objects, the URL will not have a file name name or mime type, instead it
     * will be transformed into a URL that looks like
     * `ipfs://bafy...hash/image/blob`.
     *
     * @example
     * ```js
     * const metadata = await client.store({
     *   name: 'nft.storage store test',
     *   description: 'Test ERC-1155 compatible metadata.',
     *   image: new File(['<DATA>'], 'pinpie.jpg', { type: 'image/jpg' }),
     *   properties: {
     *     custom: 'Custom data can appear here, files are auto uploaded.',
     *     file: new File(['<DATA>'], 'README.md', { type: 'text/plain' }),
     *   }
     * })
     *
     * console.log('IPFS URL for the metadata:', metadata.url)
     * console.log('metadata.json contents:\n', metadata.data)
     * console.log('metadata.json with IPFS gateway URLs:\n', metadata.embed())
     * ```
     *
     * @template {API.TokenInput} T
     * @param {T} token
     * @returns {Promise<API.Token<T>>}
     */

  }, {
    key: "store",
    value: function store(token) {
      return NFTStorage.store(this, token);
    }
  }], [{
    key: "auth",
    value: function auth(token) {
      if (!token) throw new Error('missing token');
      return {
        Authorization: "Bearer ".concat(token)
      };
    }
    /**
     * @param {API.Service} service
     * @param {Blob} blob
     * @returns {Promise<API.CIDString>}
     */

  }, {
    key: "storeBlob",
    value: function () {
      var _storeBlob = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref2, blob) {
        var endpoint, token, url, request, result;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                endpoint = _ref2.endpoint, token = _ref2.token;
                url = new URL("upload/", endpoint);

                if (!(blob.size === 0)) {
                  _context.next = 4;
                  break;
                }

                throw new Error('Content size is 0, make sure to provide some content');

              case 4:
                _context.next = 6;
                return fetch(url.toString(), {
                  method: 'POST',
                  headers: NFTStorage.auth(token),
                  body: blob
                });

              case 6:
                request = _context.sent;
                _context.next = 9;
                return request.json();

              case 9:
                result = _context.sent;

                if (!result.ok) {
                  _context.next = 14;
                  break;
                }

                return _context.abrupt("return", result.value.cid);

              case 14:
                throw new Error(result.error.message);

              case 15:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      function storeBlob(_x, _x2) {
        return _storeBlob.apply(this, arguments);
      }

      return storeBlob;
    }()
    /**
     * @param {API.Service} service
     * @param {Blob|API.CarReader} car
     * @param {{onStoredChunk?: (size: number) => void}} [options]
     * @returns {Promise<API.CIDString>}
     */

  }, {
    key: "storeCar",
    value: function () {
      var _storeCar = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(_ref3, car) {
        var endpoint,
            token,
            _ref4,
            onStoredChunk,
            targetSize,
            splitter,
            upload,
            root,
            _iteratorAbruptCompletion,
            _didIteratorError,
            _iteratorError,
            _iterator,
            _step,
            cid,
            _args3 = arguments;

        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                endpoint = _ref3.endpoint, token = _ref3.token;
                _ref4 = _args3.length > 2 && _args3[2] !== undefined ? _args3[2] : {}, onStoredChunk = _ref4.onStoredChunk;
                targetSize = MAX_CHUNK_SIZE;

                if (!(car instanceof Blob)) {
                  _context3.next = 9;
                  break;
                }

                _context3.next = 6;
                return TreewalkCarSplitter.fromBlob(car, targetSize);

              case 6:
                _context3.t0 = _context3.sent;
                _context3.next = 10;
                break;

              case 9:
                _context3.t0 = new TreewalkCarSplitter(car, targetSize);

              case 10:
                splitter = _context3.t0;
                upload = transform(MAX_CONCURRENT_UPLOADS, /*#__PURE__*/function () {
                  var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(
                  /** @type {AsyncIterable<Uint8Array>} */
                  car) {
                    var carParts, _iteratorAbruptCompletion2, _didIteratorError2, _iteratorError2, _iterator2, _step2, part, carFile, res;

                    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                      while (1) {
                        switch (_context2.prev = _context2.next) {
                          case 0:
                            carParts = [];
                            _iteratorAbruptCompletion2 = false;
                            _didIteratorError2 = false;
                            _context2.prev = 3;
                            _iterator2 = _asyncIterator(car);

                          case 5:
                            _context2.next = 7;
                            return _iterator2.next();

                          case 7:
                            if (!(_iteratorAbruptCompletion2 = !(_step2 = _context2.sent).done)) {
                              _context2.next = 13;
                              break;
                            }

                            part = _step2.value;
                            carParts.push(part);

                          case 10:
                            _iteratorAbruptCompletion2 = false;
                            _context2.next = 5;
                            break;

                          case 13:
                            _context2.next = 19;
                            break;

                          case 15:
                            _context2.prev = 15;
                            _context2.t0 = _context2["catch"](3);
                            _didIteratorError2 = true;
                            _iteratorError2 = _context2.t0;

                          case 19:
                            _context2.prev = 19;
                            _context2.prev = 20;

                            if (!(_iteratorAbruptCompletion2 && _iterator2.return != null)) {
                              _context2.next = 24;
                              break;
                            }

                            _context2.next = 24;
                            return _iterator2.return();

                          case 24:
                            _context2.prev = 24;

                            if (!_didIteratorError2) {
                              _context2.next = 27;
                              break;
                            }

                            throw _iteratorError2;

                          case 27:
                            return _context2.finish(24);

                          case 28:
                            return _context2.finish(19);

                          case 29:
                            carFile = new Blob(carParts, {
                              type: 'application/car'
                            });
                            _context2.next = 32;
                            return pRetry(function () {
                              return NFTStorage.storeBlob({
                                endpoint: endpoint,
                                token: token
                              }, carFile);
                            }, {
                              retries: MAX_STORE_RETRIES
                            });

                          case 32:
                            res = _context2.sent;
                            onStoredChunk && onStoredChunk(carFile.size);
                            return _context2.abrupt("return", res);

                          case 35:
                          case "end":
                            return _context2.stop();
                        }
                      }
                    }, _callee2, null, [[3, 15, 19, 29], [20,, 24, 28]]);
                  }));

                  return function (_x5) {
                    return _ref5.apply(this, arguments);
                  };
                }());
                _iteratorAbruptCompletion = false;
                _didIteratorError = false;
                _context3.prev = 14;
                _iterator = _asyncIterator(upload(splitter.cars()));

              case 16:
                _context3.next = 18;
                return _iterator.next();

              case 18:
                if (!(_iteratorAbruptCompletion = !(_step = _context3.sent).done)) {
                  _context3.next = 24;
                  break;
                }

                cid = _step.value;
                root = cid;

              case 21:
                _iteratorAbruptCompletion = false;
                _context3.next = 16;
                break;

              case 24:
                _context3.next = 30;
                break;

              case 26:
                _context3.prev = 26;
                _context3.t1 = _context3["catch"](14);
                _didIteratorError = true;
                _iteratorError = _context3.t1;

              case 30:
                _context3.prev = 30;
                _context3.prev = 31;

                if (!(_iteratorAbruptCompletion && _iterator.return != null)) {
                  _context3.next = 35;
                  break;
                }

                _context3.next = 35;
                return _iterator.return();

              case 35:
                _context3.prev = 35;

                if (!_didIteratorError) {
                  _context3.next = 38;
                  break;
                }

                throw _iteratorError;

              case 38:
                return _context3.finish(35);

              case 39:
                return _context3.finish(30);

              case 40:
                return _context3.abrupt("return", root);

              case 41:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, null, [[14, 26, 30, 40], [31,, 35, 39]]);
      }));

      function storeCar(_x3, _x4) {
        return _storeCar.apply(this, arguments);
      }

      return storeCar;
    }()
    /**
     * @param {API.Service} service
     * @param {Iterable<File>} files
     * @returns {Promise<API.CIDString>}
     */

  }, {
    key: "storeDirectory",
    value: function () {
      var _storeDirectory = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(_ref6, files) {
        var endpoint, token, url, body, size, _iterator3, _step3, file, response, result;

        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                endpoint = _ref6.endpoint, token = _ref6.token;
                url = new URL("upload/", endpoint);
                body = new FormData();
                size = 0;
                _iterator3 = _createForOfIteratorHelper(files);

                try {
                  for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
                    file = _step3.value;
                    body.append('file', file, file.name);
                    size += file.size;
                  }
                } catch (err) {
                  _iterator3.e(err);
                } finally {
                  _iterator3.f();
                }

                if (!(size === 0)) {
                  _context4.next = 8;
                  break;
                }

                throw new Error('Total size of files should exceed 0, make sure to provide some content');

              case 8:
                _context4.next = 10;
                return fetch(url.toString(), {
                  method: 'POST',
                  headers: NFTStorage.auth(token),
                  body: body
                });

              case 10:
                response = _context4.sent;
                _context4.next = 13;
                return response.json();

              case 13:
                result = _context4.sent;

                if (!result.ok) {
                  _context4.next = 18;
                  break;
                }

                return _context4.abrupt("return", result.value.cid);

              case 18:
                throw new Error(result.error.message);

              case 19:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }));

      function storeDirectory(_x6, _x7) {
        return _storeDirectory.apply(this, arguments);
      }

      return storeDirectory;
    }()
    /**
     * @template {API.TokenInput} T
     * @param {API.Service} service
     * @param {T} metadata
     * @returns {Promise<API.Token<T>>}
     */

  }, {
    key: "store",
    value: function () {
      var _store = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(_ref7, metadata) {
        var endpoint, token, url, body, paths, response, result, value;
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                endpoint = _ref7.endpoint, token = _ref7.token;
                validateERC1155(metadata);
                url = new URL("store/", endpoint);
                body = Token.encode(metadata);
                paths = new Set(body.keys());
                _context5.next = 7;
                return fetch(url.toString(), {
                  method: 'POST',
                  headers: NFTStorage.auth(token),
                  body: body
                });

              case 7:
                response = _context5.sent;
                _context5.next = 10;
                return response.json();

              case 10:
                result = _context5.sent;

                if (!(result.ok === true)) {
                  _context5.next = 16;
                  break;
                }

                value = result.value;
                return _context5.abrupt("return", Token.decode(value, paths));

              case 16:
                throw new Error(result.error.message);

              case 17:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }));

      function store(_x8, _x9) {
        return _store.apply(this, arguments);
      }

      return store;
    }()
    /**
     * @param {API.Service} service
     * @param {string} cid
     * @returns {Promise<API.StatusResult>}
     */

  }, {
    key: "status",
    value: function () {
      var _status = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(_ref8, cid) {
        var endpoint, token, url, response, result;
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                endpoint = _ref8.endpoint, token = _ref8.token;
                url = new URL("".concat(cid, "/"), endpoint);
                _context6.next = 4;
                return fetch(url.toString(), {
                  method: 'GET',
                  headers: NFTStorage.auth(token)
                });

              case 4:
                response = _context6.sent;
                _context6.next = 7;
                return response.json();

              case 7:
                result = _context6.sent;

                if (!result.ok) {
                  _context6.next = 12;
                  break;
                }

                return _context6.abrupt("return", {
                  cid: result.value.cid,
                  deals: decodeDeals(result.value.deals),
                  size: result.value.size,
                  pin: decodePin(result.value.pin),
                  created: new Date(result.value.created)
                });

              case 12:
                throw new Error(result.error.message);

              case 13:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }));

      function status(_x10, _x11) {
        return _status.apply(this, arguments);
      }

      return status;
    }()
    /**
     * @param {API.PublicService} service
     * @param {string} cid
     * @returns {Promise<API.CheckResult>}
     */

  }, {
    key: "check",
    value: function () {
      var _check = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7(_ref9, cid) {
        var endpoint, url, response, result;
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                endpoint = _ref9.endpoint;
                url = new URL("check/".concat(cid, "/"), endpoint);
                _context7.next = 4;
                return fetch(url.toString());

              case 4:
                response = _context7.sent;
                _context7.next = 7;
                return response.json();

              case 7:
                result = _context7.sent;

                if (!result.ok) {
                  _context7.next = 12;
                  break;
                }

                return _context7.abrupt("return", {
                  cid: result.value.cid,
                  deals: decodeDeals(result.value.deals),
                  pin: result.value.pin
                });

              case 12:
                throw new Error(result.error.message);

              case 13:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }));

      function check(_x12, _x13) {
        return _check.apply(this, arguments);
      }

      return check;
    }()
    /**
     * @param {API.Service} service
     * @param {string} cid
     * @returns {Promise<void>}
     */

  }, {
    key: "delete",
    value: function () {
      var _delete2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8(_ref10, cid) {
        var endpoint, token, url, response, result;
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                endpoint = _ref10.endpoint, token = _ref10.token;
                url = new URL("".concat(cid, "/"), endpoint);
                _context8.next = 4;
                return fetch(url.toString(), {
                  method: 'DELETE',
                  headers: NFTStorage.auth(token)
                });

              case 4:
                response = _context8.sent;
                _context8.next = 7;
                return response.json();

              case 7:
                result = _context8.sent;

                if (result.ok) {
                  _context8.next = 10;
                  break;
                }

                throw new Error(result.error.message);

              case 10:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }));

      function _delete(_x14, _x15) {
        return _delete2.apply(this, arguments);
      }

      return _delete;
    }()
  }]);

  return NFTStorage;
}();
/**
 * @param {API.TokenInput} metadata
 */


var validateERC1155 = function validateERC1155(_ref11) {
  var name = _ref11.name,
      description = _ref11.description,
      image = _ref11.image,
      decimals = _ref11.decimals;

  // Just validate that expected fields are present
  if (typeof name !== 'string') {
    throw new TypeError('string property `name` identifying the asset is required');
  }

  if (typeof description !== 'string') {
    throw new TypeError('string property `description` describing asset is required');
  }

  if (!(image instanceof Blob)) {
    throw new TypeError('property `image` must be a Blob or File object');
  } else if (!image.type.startsWith('image/')) {
    console.warn("According to ERC721 Metadata JSON Schema 'image' must have 'image/*' mime type.\n\nFor better interoperability we would highly recommend storing content with different mime type under 'properties' namespace e.g. `properties: { video: file }` and using 'image' field for storing a preview image for it instead.\n\nFor more context please see ERC-721 specification https://eips.ethereum.org/EIPS/eip-721");
  }

  if (typeof decimals !== 'undefined' && typeof decimals !== 'number') {
    throw new TypeError('property `decimals` must be an integer value');
  }
};
/**
 * @param {API.Deal[]} deals
 * @returns {API.Deal[]}
 */


var decodeDeals = function decodeDeals(deals) {
  return deals.map(function (deal) {
    var _dealExpiration$dealA = _objectSpread({
      dealExpiration: null,
      dealActivation: null
    }, deal),
        dealActivation = _dealExpiration$dealA.dealActivation,
        dealExpiration = _dealExpiration$dealA.dealExpiration,
        lastChanged = _dealExpiration$dealA.lastChanged;

    return _objectSpread(_objectSpread(_objectSpread({}, deal), {}, {
      lastChanged: new Date(lastChanged)
    }, dealActivation && {
      dealActivation: new Date(dealActivation)
    }), dealExpiration && {
      dealExpiration: new Date(dealExpiration)
    });
  });
};
/**
 * @param {API.Pin} pin
 * @returns {API.Pin}
 */


var decodePin = function decodePin(pin) {
  return _objectSpread(_objectSpread({}, pin), {}, {
    created: new Date(pin.created)
  });
};

var TokenModel = Token.Token;
export { TokenModel as Token };
export { NFTStorage, File, Blob, FormData, toGatewayURL };
/**
 * Just to verify API compatibility.
 * @type {API.API}
 */

var api = NFTStorage;
void api;